import { captureError } from '../lib/sentry';

/**
 * Sanitize a Supabase error message for user display.
 * Strips table names, column names, constraint names, and SQL details.
 * Returns a generic message for security.
 */
export function sanitizeErrorMessage(error) {
  if (!error) return 'An unexpected error occurred.';
  const msg = typeof error === 'string' ? error : error.message || '';

  // Known safe messages to pass through
  if (msg.includes('Invalid') && msg.includes('transition')) return msg; // State machine errors are user-friendly
  if (msg.includes('duplicate') || msg.includes('already exists')) return 'This record already exists.';
  if (msg.includes('violates not-null')) return 'A required field is missing.';
  if (msg.includes('violates check')) return 'One or more values are outside the allowed range.';
  if (msg.includes('violates foreign key')) return 'A referenced record could not be found.';
  if (msg.includes('permission denied')) return 'You do not have permission for this action.';
  if (msg.includes('JWT')) return 'Your session has expired. Please log in again.';

  // Default: generic message (hide DB internals)
  return 'Operation failed. Please try again or contact support.';
}

/**
 * safeSupabase — Safe wrappers for Supabase operations.
 *
 * safeQuery(queryPromise, label)  — For reads. Logs + returns { data, error }.
 * safeMutation(mutationPromise, label) — For writes. Logs + fires toast on failure.
 *
 * Usage:
 *   import { safeQuery, safeMutation } from '../utils/safeSupabase';
 *
 *   // Read:
 *   const { data, error } = await safeQuery(
 *     supabase.from('job').select('*').eq('status', 'Open'),
 *     'fetchOpenJobs'
 *   );
 *
 *   // Write:
 *   const { data, error } = await safeMutation(
 *     supabase.from('assignment').update({ status: 'Active' }).eq('id', assignId),
 *     'activateAssignment'
 *   );
 */

/**
 * Safe wrapper for Supabase read queries.
 * - Always returns { data, error } — never throws
 * - Logs errors with label context
 * - Returns { data: null, error } on failure
 */
export async function safeQuery(queryPromise, label = 'query') {
  try {
    const result = await queryPromise;
    if (result.error) {
      console.error(`[safeQuery:${label}]`, result.error.message);
      captureError(new Error(result.error.message), { label, type: 'query' });
    }
    return result;
  } catch (err) {
    console.error(`[safeQuery:${label}] Exception:`, err.message);
    captureError(err, { label, type: 'query' });
    return { data: null, error: err };
  }
}

/**
 * Safe wrapper for Supabase write mutations.
 * - Always returns { data, error } — never throws
 * - Logs errors with label context
 * - Fires global toast event on failure so user sees feedback
 * - Returns { data: null, error } on failure
 */
export async function safeMutation(mutationPromise, label = 'mutation') {
  try {
    const result = await mutationPromise;
    if (result.error) {
      console.error(`[safeMutation:${label}]`, result.error.message);
      captureError(new Error(result.error.message), { label, type: 'mutation' });
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('juuni:error', {
          detail: { message: sanitizeErrorMessage(result.error) },
        }));
      }
    }
    return result;
  } catch (err) {
    console.error(`[safeMutation:${label}] Exception:`, err.message);
    captureError(err, { label, type: 'mutation' });
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('juuni:error', {
        detail: { message: sanitizeErrorMessage(err) },
      }));
    }
    return { data: null, error: err };
  }
}

/**
 * Check if the browser is online before attempting a write.
 * Returns true if online (safe to proceed), false if offline.
 * Fires toast if offline.
 */
export function requireOnline(label = 'operation') {
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    console.warn(`[requireOnline:${label}] Blocked — browser is offline`);
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('juuni:error', {
        detail: { message: "You're offline. This action requires an internet connection." },
      }));
    }
    return false;
  }
  return true;
}
